import { z } from 'zod';
import apiClient from '../client';
import { PaymentHistoryResponse } from '@/types';

const validateCouponSchema = z.object({
  code: z.string(),
  orderAmount: z.number(),
});

const couponValidationSchema = z.object({
  coupon: z.object({
    id: z.number(),
    code: z.string(),
    description: z.string(),
    discountType: z.enum(['percentage', 'fixed']),
    discountValue: z.number(),
    minOrderAmount: z.number(),
    maxDiscountAmount: z.number(),
    usageLimit: z.number(),
    usageCount: z.number(),
    validFrom: z.string(),
    validUntil: z.string(),
  }),
  discountAmount: z.number(),
});

const couponValidationResponseSchema = z.object({
  success: z.boolean(),
  data: couponValidationSchema,
  message: z.string(),
});

export type ValidateCoupon = z.infer<typeof validateCouponSchema>;
export type CouponValidation = z.infer<typeof couponValidationSchema>;
export type CouponValidationResponse = z.infer<typeof couponValidationResponseSchema>;

// Payment discount settings types
export interface PaymentDiscountInfo {
  cryptoDiscountPercent: number;
  discountText: string;
  isActive: boolean;
}

export interface PaymentDiscountResponse {
  success: boolean;
  data: PaymentDiscountInfo;
  message: string;
}

export interface TradeMateOfferInfo {
  isActive: boolean;
}

const createOrderSchema = z.object({
  amount: z.number(),
  email: z.string().email(),
  platform: z.string(),
  challenge: z.string(),
  balance: z.number(),
  addOns: z.object({
    fastPayout7Day: z.boolean(),
    profitSplit100: z.boolean(),
    accountProtection: z.boolean(),
    secondAccountHalfOff: z.boolean(),
    scalingPlan: z.boolean(),
    payoutProtection: z.boolean(),
  }),
});

// const captureOrderSchema = z.object({
//   orderId: z.string(),
//   paymentId: z.string(),
//   email: z.string().email(),
//   platform: z.string(),
//   challenge: z.string(),
//   balance: z.number(),
//   addOns: z.array(z.string())
// });

export const payments = {
  createOrder: async (data: z.infer<typeof createOrderSchema>) => {
    const validated = createOrderSchema.parse(data);
    const response = await apiClient.post('/payment/orders', validated);
    return response.data;
  },

  captureOrder: async (orderId: string) => {
    const response = await apiClient.post(`/payment/orders/${orderId}/capture`);
    return response.data;
  },

  createOrderRecord: async (orderId: string) => {
    const response = await apiClient.post(`/payment/orders/${orderId}/create`);
    return response.data;
  },

  createWhiteLabelPayment: async (data: any) => {
    const response = await apiClient.post('/payment/white-label/create', data);
    return response.data;
  },

  // getPaymentStatus: async (paymentId: string) => {
  //   const response = await apiClient.get(`/payment/status/${paymentId}`);
  //   return response.data;
  // },

  getPaymentHistory: async () => {
    const response = await apiClient.get('/payment/history');
    return response.data;
  },

  // New v2 payment endpoints
  createPaymentV2: async (data: any) => {
    const response = await apiClient.post('/payment/v2/create', data);
    return response.data;
  },

  getPaymentStatus: async (trackId: string) => {
    const response = await apiClient.post('/payment/v2/status', { trackId }, {
      timeout: 60000
    });
    return response.data;
  },

  getBilling: async (email: string) => {
    const response = await apiClient.post('/payment/get-balance', { email });
    return response.data;
  },

  // Payment history V2 with filters
  getHistoryV2: async (params: {
    userId?: string;
    status?: string;
    method?: string;
    platform?: string;
    challenge?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    size?: number;
  } = {}) => {
    const response = await apiClient.get<PaymentHistoryResponse>('/payment/v2/history', {
      params,
    });
    return response.data;
  },

  // Coupon validation
  validateCoupon: async (data: ValidateCoupon) => {
    const validated = validateCouponSchema.parse(data);
    const response = await apiClient.post('/coupon/validate', validated);
    return response.data;
  },

  // Get active discount info (public endpoint)
  getActiveDiscountInfo: async (): Promise<PaymentDiscountResponse> => {
    const response = await apiClient.get<PaymentDiscountResponse>('/public/payment-discount-settings');
    return response.data;
  },

  // Get Paytiko enabled flag (public endpoint)
  getPaytikoSettings: async (): Promise<{ success: boolean; data: { isEnabled: boolean }; message: string }> => {
    const response = await apiClient.get<{ success: boolean; data: { isEnabled: boolean }; message: string }>('/public/paytiko-settings');
    return response.data;
  },

  getTradeMateOfferSettings: async (): Promise<{ success: boolean; data: TradeMateOfferInfo; message: string }> => {
    const response = await apiClient.get<{ success: boolean; data: TradeMateOfferInfo; message: string }>('/public/trademate-offer-settings');
    return response.data;
  },

  // Create discount for discounted account reset
  createDiscount: async (data: { accountId: string; amount: number; platform: string; balance: number; challenge: string; }) => {
    const response = await apiClient.post('/payment/v2/create-discount', data);
    return response.data;
  },

  // Paytiko payment endpoint
  createPaytikoPayment: async (data: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    country: string;
    currency: string;
    platform: string;
    balance: number;
    challenge: string;
    amount: number;
    street: string;
    city: string;
    region: string;
    zipCode: string;
    dateOfBirth: string;
    creditCardOnly?: boolean;
  }) => {
    const response = await apiClient.post('/payments/paytiko/create', data);
    return response.data;
  },
};
